/**
 * The shell: role-aware nav, the budget-year selector, and the account area.
 *
 * "Role-aware" is not cosmetic. A `dept_manager` never sees an admin section — not greyed out,
 * not present. The capability flags come from `GET /me` so the browser never re-derives the
 * permission matrix; every route re-checks server-side anyway, and this is about not offering
 * a door that will be shut in someone's face.
 */
import { NavLink, Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, devIdentity, setDevIdentity } from '@/lib/api';
import { useSession, sgtYear } from '@/hooks/session';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui';
import { Bell } from '@/components/Bell';
import { FeedbackButton } from '@/components/Feedback';
import { ROLE_LABEL } from '@/lib/policy';

interface NavItem {
  to: string;
  label: string;
  show: (session: ReturnType<typeof useSession>) => boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', show: () => true },
  { to: '/proposals', label: 'Proposals', show: () => true },
  { to: '/claims', label: 'Claims', show: () => true },
  {
    to: '/approvals',
    label: 'Waiting on you',
    // HR reaches the same records through their own queues, so this would be a duplicate for
    // them — and a Sponsor's own submissions skip the queue entirely (D-39).
    show: (session) => session.me.capabilities.endorse && !session.isHr,
  },
  { to: '/hr/review', label: 'HR review', show: (session) => session.isHr },
  {
    to: '/hr/director',
    label: 'Director approvals',
    // Shown to every admin, not just Directors: an admin needs to see that the queue is
    // waiting on somebody. Whether *they* can clear it is stated on the screen.
    show: (session) => session.isHr,
  },
  { to: '/hr/payments', label: 'Payments', show: (session) => session.isHr },
  { to: '/hr/employees', label: 'Employees', show: (session) => session.isHr },
  // Reports are scoped, so a manager sees their own departments — same rule as the lists.
  { to: '/reports', label: 'Reports', show: () => true },
  { to: '/hr/budget', label: 'Budget', show: (session) => session.isHr },
  { to: '/hr/setup', label: 'Setup', show: (session) => session.isHr },
  { to: '/hr/users', label: 'Users', show: (session) => session.me.capabilities.manage_users },
  { to: '/hr/operations', label: 'Operations', show: (session) => session.isHr },
  { to: '/hr/feedback', label: 'Feedback', show: (session) => session.isHr },
];

export function Shell() {
  const session = useSession();
  const years = useQuery({ queryKey: ['budget-years'], queryFn: api.budgetYears });

  const options = years.data?.years.map((year) => year.year) ?? [sgtYear()];
  const selected = years.data?.years.find((year) => year.year === session.year);

  return (
    <div className="min-h-dvh">
      <header className="border-border bg-background sticky top-0 z-10 border-b">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
          <div className="mr-auto">
            <p className="text-muted-foreground text-[0.7rem] font-medium tracking-wide uppercase">
              Ley Choon Group · HR035
            </p>
            <p className="text-sm font-semibold">Department Bonding</p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Budget year</span>
            <select
              className="border-input bg-background rounded-md border px-2 py-1 text-sm"
              value={session.year}
              onChange={(event) => session.setYear(Number(event.target.value))}
            >
              {options.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-3 text-sm">
            <Bell />
            <div className="text-right">
              <p className="font-medium">
                {session.me.employee?.full_name ?? session.me.user.email}
              </p>
              <p className="text-muted-foreground text-xs">
                {ROLE_LABEL[session.me.user.role] ?? session.me.user.role}
                {session.me.capabilities.hr_director ? ' · HR Director' : ''}
              </p>
            </div>
            {/* Directly beside the account area, on every screen — the one thing the
                `in-app-feedback` pattern fixes about placement. */}
            <FeedbackButton appVersion={session.meta.version} />
            {session.meta.auth === 'dev-stub' ? <DevIdentity /> : null}
          </div>
        </div>

        <nav className="mx-auto max-w-7xl overflow-x-auto px-4">
          <ul className="flex gap-1 whitespace-nowrap">
            {NAV.filter((item) => item.show(session)).map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'inline-block border-b-2 px-3 py-2 text-sm',
                      isActive
                        ? 'border-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground border-transparent',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      {/*
        D-23, on every screen rather than on the budget page alone.
        Until HR confirms the pre-app history is in, every figure in the app may be too high —
        the entitlement is shown in full while part of it has already been spent under the email
        process. A manager planning against an overstated pool is the failure that costs money, so
        the caveat belongs wherever a figure is, which is everywhere.
      */}
      {selected && selected.prior_period_loaded_at === null ? (
        <p className="border-b border-[var(--color-warning)]/50 px-4 py-2 text-center text-xs">
          <strong>{selected.year} figures may be too high.</strong> Events run before this app
          existed have not been loaded yet, so the entitlement shown includes budget that may
          already be spent. HR is correcting it as the history arrives.
        </p>
      ) : null}

      {session.meta.devBypass ? (
        <p className="border-destructive/50 text-destructive border-b bg-transparent px-4 py-2 text-center text-xs">
          <strong>AUTH_DEV_BYPASS is active.</strong> Requests are attributed to an unverified local
          identity. This build must never reach production.
        </p>
      ) : null}

      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

/** Switching identity while building against the stub. Absent on any other verifier. */
function DevIdentity() {
  const current = devIdentity();
  return (
    <Badge tone="warn" title="Development identity — no password, no verification">
      <button
        type="button"
        className="underline underline-offset-2"
        onClick={() => {
          setDevIdentity(null);
          globalThis.location.reload();
        }}
      >
        dev: {current ?? 'default'}
      </button>
    </Badge>
  );
}
